import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import Link from "@cloudscape-design/components/link"
import SpaceBetween from "@cloudscape-design/components/space-between"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { type ReactNode, useMemo } from "react"
import { useNavigate } from "react-router-dom"

import type { CapabilitySet } from "@/services/capabilitySetsApi"
import type { GrantItem, GrantTarget } from "@/services/grantsApi"
import { useAuth } from "@/contexts/AuthContext"
import { useRevokeGrant } from "@/hooks/queries/useGrants"
import { isForbidden, parseApiErrorMessage } from "@/utils/apiErrors"

import { sameEmail } from "@/utils/email"
import CapabilitySetSummary from "./CapabilitySetSummary"
import { useConfirmedRevoke } from "./useConfirmedRevoke"

interface GrantsTableProps {
  target: GrantTarget
  grants: GrantItem[]
  loading: boolean
  error: Error | null
  /**
   * Configured capability sets by name, to describe each grant's set.
   */
  setsByName: ReadonlyMap<string, CapabilitySet>
  title: string
  description: ReactNode
  /**
   * Header actions, e.g. the grant button.
   */
  actions?: ReactNode
  /**
   * Whether the caller may revoke. When false the table is read-only with no Actions column.
   */
  canManage: boolean
  /**
   * Text shown above the table.
   */
  note?: string
  /**
   * Grants inherited from a parent, listed read-only after the resource's own grants.
   */
  inherited?: InheritedGrants
  empty: string
}

/**
 * Grants placed on a parent resource that cascade to the one shown.
 */
export interface InheritedGrants {
  grants: GrantItem[]
  /**
   * Where the grants come from, shown in the "Applies to" column, e.g. "Study".
   */
  source: string
  /**
   * Route of the page that manages these grants.
   */
  manageHref: string
}

interface GrantRow {
  grant: GrantItem
  inherited: boolean
}

/**
 * Table of a study's or DTA's grants, with a confirmed revoke per grant.
 */
export default function GrantsTable({
  target,
  grants,
  loading,
  error,
  setsByName,
  title,
  description,
  actions,
  canManage,
  note,
  inherited,
  empty,
}: GrantsTableProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const revokeGrant = useRevokeGrant(target)
  const { requestRevoke, modal } = useConfirmedRevoke({
    revoke: revokeGrant.mutate,
    describe: (grant: GrantItem) => `${setLabel(grant, setsByName)} from ${grant.email}`,
    revokedMessage: (grant: GrantItem) =>
      `${grant.email} no longer has ${setLabel(grant, setsByName)}`,
  })

  const rows = useMemo<GrantRow[]>(
    () => [
      ...grants.map((grant) => ({ grant, inherited: false })),
      ...(inherited?.grants ?? []).map((grant) => ({ grant, inherited: true })),
    ],
    [grants, inherited],
  )
  // Grey out inherited rows to show they are managed elsewhere.
  const text = (row: GrantRow, value: string) =>
    row.inherited ? <Box color="text-status-inactive">{value}</Box> : value

  const actionsColumn: TableProps.ColumnDefinition<GrantRow> = {
    id: "actions",
    header: "Actions",
    cell: (row) =>
      row.inherited && inherited ? (
        <Link
          href={inherited.manageHref}
          onFollow={(e) => {
            e.preventDefault()
            navigate(inherited.manageHref)
          }}
        >
          {`Manage on ${inherited.source.toLowerCase()}`}
        </Link>
      ) : (
        <Button
          variant="inline-link"
          disabled={sameEmail(row.grant.email, user?.email)}
          disabledReason="You can't revoke your own access"
          onClick={() => requestRevoke(row.grant)}
          ariaLabel={`Revoke ${setLabel(row.grant, setsByName)} from ${row.grant.email}`}
        >
          Revoke
        </Button>
      ),
  }

  if (error) {
    return (
      <Alert type={isForbidden(error) ? "info" : "error"} header={title}>
        {isForbidden(error)
          ? "You don't have permission to see who has access here."
          : parseApiErrorMessage(error, "Failed to load access")}
      </Alert>
    )
  }

  return (
    <>
      <Container
        header={
          <Header variant="h2" description={description} actions={actions}>
            {title}
          </Header>
        }
      >
        <SpaceBetween size="m">
          {note && <Alert type="info">{note}</Alert>}
          <Table
            items={rows}
            loading={loading}
            loadingText="Loading access"
            trackBy={({ grant, inherited: fromParent }) =>
              `${fromParent ? "inherited" : "own"}#${grant.principal_id}#${grant.capability_set}`
            }
            empty={<Box color="text-body-secondary">{empty}</Box>}
            variant="embedded"
            wrapLines
            columnDefinitions={[
              { id: "person", header: "Person", cell: (row) => text(row, row.grant.email) },
              {
                id: "set",
                header: "Capability set",
                cell: ({ grant }) => (
                  <CapabilitySetSummary
                    name={grant.capability_set}
                    set={setsByName.get(grant.capability_set)}
                  />
                ),
              },
              ...(inherited
                ? [
                    {
                      id: "applies_to",
                      header: "Applies to",
                      cell: (row: GrantRow) =>
                        row.inherited
                          ? text(row, `${inherited.source} (inherited)`)
                          : `This ${target.type === "dta" ? "DTA" : "study"}`,
                    },
                  ]
                : []),
              {
                id: "status",
                header: "Status",
                cell: ({ grant }) =>
                  grant.status === "active" ? (
                    <StatusIndicator type="success">Active</StatusIndicator>
                  ) : (
                    <StatusIndicator type="pending">Pending</StatusIndicator>
                  ),
              },
              {
                id: "granted_by",
                header: "Granted by",
                cell: (row) => text(row, row.grant.granted_by),
              },
              {
                id: "granted_at",
                header: "Granted",
                cell: (row) => text(row, row.grant.granted_at.slice(0, 10)),
              },
              ...(canManage ? [actionsColumn] : []),
            ]}
          />
        </SpaceBetween>
      </Container>
      {modal}
    </>
  )
}

function setLabel(grant: GrantItem, setsByName: ReadonlyMap<string, CapabilitySet>): string {
  return setsByName.get(grant.capability_set)?.display_name ?? grant.capability_set
}
